import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Utensils,
  Star,
  AlertCircle,
  ChefHat
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getRestaurantDashboardStats,
  getRestaurantOrders,
  getReviews,
  getLedgerBalance,
  getLedgerPayouts,
  formatCurrency,
  handleRestaurantApiError
} from '../../../services/restaurantApi';
import SkeletonImage from '../../skeletons/SkeletonImage';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const STATUS_BADGE = {
  pending: { bg: '#F4F1E2', fg: '#393A30', label: 'En attente' },
  preparing: { bg: '#FEF0EA', fg: '#A32C0F', label: 'En cuisson' },
  confirmed: { bg: '#FEF0EA', fg: '#A32C0F', label: 'En cuisson' },
  paid: { bg: '#FEF0EA', fg: '#A32C0F', label: 'En cuisson' },
  ready: { bg: '#DDEECB', fg: '#054625', label: 'Prête' },
  delivered: { bg: 'transparent', fg: '#051004', border: '1px solid rgba(5,16,4,0.2)', label: 'Livrée' },
  cancelled: { bg: '#FEF0EA', fg: '#A32C0F', label: 'Annulée' },
};

const NewRestaurantDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalDishes: 0,
    averageRating: 0,
    activeEmployees: 0,
    pendingOrders: 0,
    completedOrders: 0,
    todayRevenue: 0,
    totalCustomers: 0,
    restaurantReviewsCount: 0,
    dishReviewsCount: 0,
    revenueChangePercent: 0,
    ordersChangePercent: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topDishes, setTopDishes] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(null);
  const [lastPayout, setLastPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Charger les vraies données du dashboard
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Charger les statistiques du dashboard
        const statsData = await getRestaurantDashboardStats();
        if (statsData?.error) {
          setError(String(statsData.error));
        }
        const safeNum = (v, fallback = 0) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : fallback;
        };
        setStats({
          totalOrders: statsData.total_orders ?? 0,
          totalRevenue: parseFloat(statsData.total_revenue) || 0,
          totalDishes: statsData.total_dishes ?? 0,
          averageRating: safeNum(statsData.average_rating, 0),
          activeEmployees: statsData.active_employees ?? 0,
          pendingOrders: statsData.pending_orders ?? 0,
          completedOrders: statsData.completed_orders ?? 0,
          todayRevenue: parseFloat(statsData.today_revenue) || 0,
          totalCustomers: statsData.total_customers ?? 0,
          restaurantReviewsCount:
            statsData.total_review_count ??
            statsData.restaurant_reviews_count ??
            0,
          dishReviewsCount: statsData.dish_reviews_count ?? 0,
          revenueChangePercent: safeNum(statsData.revenue_change_percent, 0),
          ordersChangePercent: safeNum(statsData.orders_change_percent, 0)
        });

        // Commandes pour graphiques / stats (échantillon récent suffisant)
        const ordersData = await getRestaurantOrders({ limit: 50 });
        const list = Array.isArray(ordersData) ? ordersData : (ordersData.orders || []);
        setRecentOrders(list);

        // Charger les plats populaires
        setTopDishes(statsData.top_dishes || []);

        // Charger les 2 derniers avis pour le tableau de bord
        try {
          const reviewsData = await getReviews();
          const list = reviewsData?.reviews ?? (Array.isArray(reviewsData) ? reviewsData : []);
          setRecentReviews(Array.isArray(list) ? list.slice(0, 2) : []);
        } catch (_) {
          setRecentReviews([]);
        }

        // Charger le résumé du portefeuille (ledger, best effort)
        try {
          const [balanceData, payoutsData] = await Promise.all([
            getLedgerBalance(),
            getLedgerPayouts({ limit: 1 }),
          ]);
          setLedgerBalance(balanceData || null);
          setLastPayout(Array.isArray(payoutsData) && payoutsData.length > 0 ? payoutsData[0] : null);
        } catch (_) {
          setLedgerBalance(null);
          setLastPayout(null);
        }

        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement du dashboard:', error);

        // Gérer les erreurs d'authentification gracieusement
        const errorMessage = handleRestaurantApiError(error);
        if (errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('Unauthorized')) {
          console.warn('Erreur d\'authentification détectée, utilisation des données par défaut');
          setError('Session expirée - actualisez la page');
        } else {
          setError(errorMessage);
        }

        // Utiliser des données par défaut en cas d'erreur
        setStats({
          totalOrders: 0,
          totalRevenue: 0,
          totalDishes: 0,
          averageRating: 0,
          activeEmployees: 0,
          pendingOrders: 0,
          completedOrders: 0,
          todayRevenue: 0,
          totalCustomers: 0,
          restaurantReviewsCount: 0,
          dishReviewsCount: 0,
          revenueChangePercent: 0,
          ordersChangePercent: 0
        });
        setRecentOrders([]);
        setTopDishes([]);
        setRecentReviews([]);
        setLedgerBalance(null);
        setLastPayout(null);

        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const buildTrendSeries = (orders) => {
    const labels = ['J-6', 'J-5', 'J-4', 'J-3', 'J-2', 'J-1', "Aujourd'hui"];
    const revenue = new Array(7).fill(0);
    const orderCounts = new Array(7).fill(0);
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 6);

    if (Array.isArray(orders) && orders.length > 0) {
      orders.forEach((o) => {
        const dateRaw = o?.created_at || o?.createdAt || o?.order_date || o?.date;
        const amount = Number(o?.total || o?.amount || 0) || 0;
        const d = dateRaw ? new Date(dateRaw) : null;
        if (!d || Number.isNaN(d.getTime())) return;
        const idx = Math.floor((d - start) / (1000 * 60 * 60 * 24));
        if (idx >= 0 && idx < 7) {
          revenue[idx] += amount;
          orderCounts[idx] += 1;
        }
      });
    }

    return { labels, revenue, orderCounts };
  };

  const trend = buildTrendSeries(recentOrders);
  const maxRevenue = Math.max(...trend.revenue, 0);
  const totalTrendRevenue = trend.revenue.reduce((s, v) => s + v, 0);
  const totalTrendOrders = trend.orderCounts.reduce((s, v) => s + v, 0);
  // Inclure paid / confirmed (souvent absents du graphique avant correction)
  const orderStatusCounts = {
    pending: recentOrders.filter((o) => o.status === 'pending').length,
    preparing: recentOrders.filter((o) =>
      ['preparing', 'confirmed', 'paid'].includes(o.status)
    ).length,
    ready: recentOrders.filter((o) => o.status === 'ready').length,
    delivered: recentOrders.filter((o) => o.status === 'delivered').length,
  };

  const isToday = (raw) => {
    if (!raw) return false;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };

  const todaysOrders = recentOrders.filter((o) => isToday(o?.created_at || o?.createdAt));
  const todayOrdersCount = todaysOrders.length;
  const todayBasket = todayOrdersCount > 0 ? stats.todayRevenue / todayOrdersCount : 0;

  const sortedOrders = [...recentOrders].sort(
    (a, b) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
  );
  const currentTicket = sortedOrders.find((o) => ['preparing', 'confirmed', 'paid'].includes(o.status));
  const kanbanCols = [
    { key: 'pending', label: 'À faire', color: 'rgba(5,16,4,0.6)', orders: sortedOrders.filter((o) => o.status === 'pending').slice(0, 2) },
    { key: 'preparing', label: 'En cours', color: TERRA, orders: sortedOrders.filter((o) => ['preparing', 'confirmed', 'paid'].includes(o.status)).slice(0, 2) },
    { key: 'ready', label: 'Prêtes', color: PRIMARY, orders: sortedOrders.filter((o) => o.status === 'ready').slice(0, 2) },
  ];

  const elapsedMinutes = (raw) => {
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  };

  const formatSlot = (raw) => {
    if (!raw) return '—';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const maxTopDishOrders = Math.max(1, ...(Array.isArray(topDishes) ? topDishes.map((d) => Number(d?.orders) || 0) : [0]));

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(18px, 2.2vw, 28px)' }}>
      {/* Header */}
      <div className="flex items-end gap-4 flex-wrap">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Aujourd'hui</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/management/menu')}
          style={{ marginLeft: 'auto', border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', minHeight: '44px', padding: '0 24px', borderRadius: '999px', whiteSpace: 'nowrap' }}
        >
          Publier un plat
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Recettes du jour</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 2.6vw, 34px)', letterSpacing: '-0.02em' }}>{formatCurrency(stats.todayRevenue)}</strong>
          {stats.revenueChangePercent !== 0 && (
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: stats.revenueChangePercent >= 0 ? PRIMARY : TERRA }}>
              {stats.revenueChangePercent >= 0 ? '▲' : '▼'} {Math.abs(stats.revenueChangePercent)}% vs hier
            </span>
          )}
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Commandes</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 2.6vw, 34px)', letterSpacing: '-0.02em' }}>{todayOrdersCount}</strong>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>{stats.pendingOrders} en attente</span>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Panier moyen</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 2.6vw, 34px)', letterSpacing: '-0.02em' }}>{formatCurrency(todayBasket)}</strong>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }}>aujourd'hui</span>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Note moyenne</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 2.6vw, 34px)', letterSpacing: '-0.02em' }}>{stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '—'}</strong>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }}>{stats.restaurantReviewsCount} avis</span>
        </div>
      </div>

      {/* Recettes 7j + File de cuisine */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 'clamp(14px, 1.8vw, 22px)', alignItems: 'start' }}>

        <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Recettes · 7 jours</h2>
            <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.64)' }}>{formatCurrency(totalTrendRevenue)} cumulés</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px', alignItems: 'end', height: '150px' }}>
            {trend.revenue.map((v, i) => {
              const isLast = i === trend.revenue.length - 1;
              const h = maxRevenue > 0 ? Math.max(6, Math.round((v / maxRevenue) * 100)) : 6;
              return <span key={i} style={{ height: `${h}%`, borderRadius: '999px', background: isLast ? TERRA : '#E5E2CB' }} />;
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px', fontFamily: 'Archivo, sans-serif', fontSize: '12px', textAlign: 'center', color: 'rgba(5,16,4,0.62)' }}>
            {trend.labels.map((l) => <span key={l}>{l}</span>)}
          </div>
          <div className="flex gap-6 flex-wrap" style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.62)' }}>Commandes (période)</span>
              <strong style={{ fontSize: '16px' }}>{totalTrendOrders}</strong>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.62)' }}>Pic quotidien</span>
              <strong style={{ fontSize: '16px' }}>{formatCurrency(maxRevenue)}</strong>
            </span>
          </div>
        </section>

        <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>File de cuisine</h2>
            <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.64)' }}>
              {orderStatusCounts.pending} en attente · {orderStatusCounts.ready} prêtes
            </span>
          </div>

          {currentTicket ? (
            <div style={{ border: `2px solid ${TERRA}`, background: '#FBF9D9', borderRadius: '26px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <span className="flex items-center gap-2.5 flex-wrap">
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '21px' }}>{currentTicket.orderNumber || `#${currentTicket.id}`}</strong>
                <span style={{ background: TERRA, color: '#FCFBF5', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 13px', borderRadius: '999px' }}>En cuisson</span>
                {elapsedMinutes(currentTicket.created_at) != null && (
                  <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 700, color: TERRA }}>⏱ {elapsedMinutes(currentTicket.created_at)} min</span>
                )}
              </span>
              <span style={{ fontSize: '14.5px', color: 'rgba(5,16,4,0.72)' }}>
                {currentTicket.customer?.name || currentTicket.phone || 'Client'} · {currentTicket.items_count || (Array.isArray(currentTicket.items) ? currentTicket.items.length : 0)} plat(s)
              </span>
              <button
                type="button"
                onClick={() => navigate('/management/orders')}
                style={{ alignSelf: 'flex-start', border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '42px', padding: '0 20px', borderRadius: '999px' }}
              >
                Ouvrir la commande
              </button>
            </div>
          ) : (
            <div style={{ background: '#FBF9D9', borderRadius: '26px', padding: '20px', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
              Aucune commande en cuisson pour le moment.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
            {kanbanCols.map((col) => (
              <span key={col.key} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: col.color }}>{col.label} · {orderStatusCounts[col.key]}</span>
                {col.orders.length === 0 && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.4)' }}>—</span>}
                {col.orders.map((o) => (
                  <span key={o.id} style={{ background: '#FBF9D9', borderRadius: '16px', padding: '10px 12px', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px' }}>
                    {o.orderNumber || `#${o.id}`} · {o.items_count || (Array.isArray(o.items) ? o.items.length : 0)} plat(s)
                  </span>
                ))}
              </span>
            ))}
          </div>
        </section>
      </div>

      {/* Commandes du service */}
      <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Commandes du service</h2>
          <button
            type="button"
            onClick={() => navigate('/management/orders')}
            style={{ marginLeft: 'auto', border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#051004', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, minHeight: '40px', padding: '0 18px', borderRadius: '999px', whiteSpace: 'nowrap' }}
          >
            Voir toutes →
          </button>
        </div>
        {sortedOrders.length === 0 ? (
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>Aucune commande pour le moment.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Commande', 'Client', 'Plats', 'Créneau', 'Total', 'Statut'].map((h, i) => (
                    <th key={h} style={{ textAlign: i === 4 ? 'right' : 'left', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', padding: '0 12px 12px 0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedOrders.slice(0, 6).map((order) => {
                  const badge = STATUS_BADGE[order.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={order.id}>
                      <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontWeight: 600, fontSize: '14.5px' }}>{order.orderNumber || `#${order.id}`}</td>
                      <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14.5px' }}>{order.customer?.name || order.phone || '—'}</td>
                      <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14.5px' }}>{order.items_count || (Array.isArray(order.items) ? order.items.length : 0)}</td>
                      <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14.5px' }}>{formatSlot(order.created_at)}</td>
                      <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14.5px', textAlign: 'right' }}>{formatCurrency(order.total || 0)}</td>
                      <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 0' }}>
                        <span style={{ background: badge.bg, color: badge.fg, border: badge.border || 'none', fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, padding: '6px 13px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Ma carte + Portefeuille/Avis */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 'clamp(14px, 1.8vw, 22px)', alignItems: 'start' }}>

        <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="flex items-baseline gap-3">
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Ma carte</h2>
            <button
              type="button"
              onClick={() => navigate('/management/menu')}
              style={{ marginLeft: 'auto', border: 'none', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, padding: 0 }}
            >
              Tout gérer →
            </button>
          </div>

          {Array.isArray(topDishes) && topDishes.length > 0 ? (
            <>
              {(() => {
                const top = topDishes[0];
                const imgUrl = top?.image_url || top?.dish_image_url;
                return (
                  <div className="flex items-center gap-3.5" style={{ border: '1px solid rgba(5,16,4,0.1)', borderRadius: '24px', padding: '12px' }}>
                    <span style={{ flex: 'none', width: '66px', height: '66px', borderRadius: '18px', overflow: 'hidden', display: 'block', background: '#F4F1E2' }}>
                      {imgUrl ? (
                        <SkeletonImage src={imgUrl} alt={top?.name} width="w-full" height="h-full" className="rounded-none" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Utensils className="h-6 w-6" style={{ color: 'rgba(5,70,37,0.3)' }} aria-hidden />
                        </div>
                      )}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className="flex items-baseline gap-2.5">
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }} className="truncate">{top?.name}</strong>
                        {top?.price != null && <strong style={{ marginLeft: 'auto', fontSize: '15px', whiteSpace: 'nowrap' }}>{formatCurrency(Number(top.price) || 0)}</strong>}
                      </span>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.66)' }}>
                        {top?.orders ?? 0} commande{(top?.orders ?? 0) > 1 ? 's' : ''} · {formatCurrency(typeof top?.revenue === 'number' ? top.revenue : 0)} de CA
                      </span>
                    </span>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Plats les plus vendus</span>
                {topDishes.slice(0, 3).map((dish, i) => {
                  const pct = Math.max(6, Math.round(((Number(dish?.orders) || 0) / maxTopDishOrders) * 100));
                  return (
                    <span key={dish?.id ?? i}>
                      <span className="flex items-center justify-between" style={{ fontSize: '13.5px', marginBottom: '5px' }}>
                        <span className="truncate">{dish?.name}</span>
                        <strong>{dish?.orders ?? 0}</strong>
                      </span>
                      <span style={{ display: 'block', height: '7px', borderRadius: '999px', background: '#E5E2CB' }}>
                        <span style={{ display: 'block', height: '7px', width: `${pct}%`, borderRadius: '999px', background: i === 0 ? TERRA : i === 1 ? '#F9BAA4' : '#C7C5AC' }} />
                      </span>
                    </span>
                  );
                })}
              </div>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>Aucune donnée de vente pour l'instant.</p>
          )}
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(14px, 1.8vw, 22px)' }}>
          <div style={{ background: PRIMARY, color: '#FBF9D9', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#FFC717' }}>Solde disponible</span>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3vw, 38px)', letterSpacing: '-0.02em' }}>{formatCurrency(ledgerBalance?.available_balance || 0)}</strong>
            <span style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(251,249,217,0.8)' }}>
              {lastPayout
                ? `Dernier versement : ${formatCurrency(lastPayout.amount || 0)} le ${new Date(lastPayout.created_at).toLocaleDateString('fr-FR')}.`
                : 'Aucun versement pour le moment.'}
            </span>
            <span className="flex gap-2.5 flex-wrap" style={{ marginTop: '6px' }}>
              <button
                type="button"
                onClick={() => navigate('/management/wallet')}
                style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 22px', borderRadius: '999px', whiteSpace: 'nowrap' }}
              >
                Retirer maintenant
              </button>
              <button
                type="button"
                onClick={() => navigate('/management/wallet')}
                style={{ border: '1px solid rgba(251,249,217,0.4)', cursor: 'pointer', background: 'transparent', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 22px', borderRadius: '999px', whiteSpace: 'nowrap' }}
              >
                Relevés
              </button>
            </span>
          </div>

          <div style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="flex items-baseline gap-3">
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Avis récents</h2>
              {recentReviews.length > 0 && (
                <span style={{ marginLeft: 'auto', background: '#FFC717', color: '#051004', fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px' }}>{recentReviews.length}</span>
              )}
            </div>
            {recentReviews.length === 0 ? (
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>Aucun avis pour le moment.</p>
            ) : (
              recentReviews.map((review, index) => (
                <blockquote key={review.id || index} style={{ margin: 0, background: '#FBF9D9', borderRadius: '24px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <span className="flex items-center gap-2">
                    <Star size={14} style={{ color: TERRA }} fill="currentColor" />
                    <strong style={{ fontSize: '13.5px' }}>{review.rating ?? review.score ?? '–'}</strong>
                    <strong style={{ marginLeft: 'auto', fontSize: '14px' }}>{review.client_name || review.user_name || 'Client'}</strong>
                  </span>
                  <p className="line-clamp-2" style={{ margin: 0, fontSize: '14px', lineHeight: 1.5 }}>{review.comment || review.comment_text || '—'}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/management/reviews')}
                    style={{ alignSelf: 'flex-start', border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', minHeight: '38px', padding: '0 18px', borderRadius: '999px' }}
                  >
                    Répondre
                  </button>
                </blockquote>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Traiteur */}
      <section className="flex items-center gap-4 flex-wrap" style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)' }}>
        <span className="flex items-center gap-3">
          <ChefHat size={22} style={{ color: PRIMARY }} />
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '17px' }}>Service traiteur</strong>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.62)' }}>Demandes et formules pour événements</span>
          </span>
        </span>
        <button
          type="button"
          onClick={() => navigate('/management/catering')}
          style={{ marginLeft: 'auto', border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '42px', padding: '0 22px', borderRadius: '999px', whiteSpace: 'nowrap' }}
        >
          Accéder au traiteur
        </button>
      </section>
    </div>
  );
};

export default NewRestaurantDashboard;
